'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { listenForClickOutside } from './clickOutside';
import { type MathFieldProps, useMathFieldCommit } from './useMathFieldCommit';

export interface UseMathEditSessionOptions {
  /** The expression the document holds. */
  readonly expression: string;
  /** Whether the surface allows editing; a read-only surface never opens the field. */
  readonly editable: boolean;
  /** Writes an expression back to the document. */
  readonly onSave: (expression: string) => void;
  /** Removes the math when an edit ends empty; without it, a cleared expression is saved as `''`. */
  readonly onRemove?: () => void;
  /** Receives a save that threw; the field closes either way. */
  readonly onCommitError: (error: unknown) => void;
}

export interface MathEditSession {
  readonly isEditing: boolean;
  /** Opens the field; does nothing on a surface that is not editable. */
  readonly open: () => void;
  /** Commits what the teacher changed, then closes. */
  readonly close: () => void;
  /** Spread onto `EditableMath`. */
  readonly fieldProps: MathFieldProps;
}

/**
 * Editing math in place: a field that opens on demand (or by itself for a new, empty expression),
 * takes focus, and commits and closes when the teacher clicks outside it.
 */
export function useMathEditSession({ expression, editable, onSave, onRemove, onCommitError }: UseMathEditSessionOptions): MathEditSession {
  const [isEditing, setIsEditing] = useState(false);
  const hasAutoOpened = useRef(false);
  const { fieldProps, commit } = useMathFieldCommit({ expression, isEditing, onSave, onRemove });
  const fieldRef = fieldProps.ref;

  const open = useCallback(() => {
    if (editable) setIsEditing(true);
  }, [editable]);

  const close = useCallback(() => {
    try {
      commit();
    } catch (error) {
      onCommitError(error);
    } finally {
      setIsEditing(false);
    }
  }, [commit, onCommitError]);

  useEffect(() => {
    if (hasAutoOpened.current || !editable || expression.trim() !== '') return;
    hasAutoOpened.current = true;
    setIsEditing(true);
  }, [expression, editable]);

  useEffect(() => {
    if (!isEditing) return;
    const timeoutId = setTimeout(() => fieldRef.current?.focus(), 0);
    return () => clearTimeout(timeoutId);
  }, [isEditing, fieldRef]);

  useEffect(() => {
    if (!isEditing) return;
    return listenForClickOutside(fieldRef, close);
  }, [isEditing, fieldRef, close]);

  return { isEditing, open, close, fieldProps };
}
